import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "./api";

import { DEFAULT_CRM_STAGES, buildCrmPipeline, gatedStageLevels, stageNameOf, type CrmPipeline } from "./crmService";

/** A stage a template step can gate (the deal must be at this stage to approve it), with the next one. */
export interface GatedStage {
  level: number;
  name: string;
  next: string;
}

/** The stages a template's steps can gate in a pipeline: every forward stage but the first and the last. */
export const gatedStagesOf = (p: CrmPipeline): GatedStage[] =>
  gatedStageLevels(p).map((level) => ({ level, name: stageNameOf(p, level), next: stageNameOf(p, level + 1) }));

/** Gated stages of the Default pipeline (Proposal, Negotiations, Legal, Closed Won). */
export const DEAL_APPROVAL_STAGES = gatedStagesOf(buildCrmPipeline(DEFAULT_CRM_STAGES));

/**
 * Fixed step right after the company's Create Order stage (Default: Legal → Closed Won). It isn't
 * part of a template's own steps: the existing OMS order approvals (Order Approvers L1–L4) decide
 * it, and the deal moves to the next stage only once the order is fully approved.
 */
export const CREATE_ORDER_STEP = {
  name: "Create Order",
  description: "Available once this stage's sub steps are approved. Approved through the existing Order Approvals (L1 Ops → L2 BH → L3 Finance → L4 Margin); the deal moves to the next stage only once the order is fully approved.",
} as const;

/**
 * Stage levels in display order, with the Create Order step right after the company's Create
 * Order stage (Default: Legal 4). No Create Order stage = no such step.
 */
export const withCreateOrderStep = (levels: number[], orderStageLevel: number | null = 4): (number | "create-order")[] => {
  // `levels` come in the deal's stage order (the server sorts steps by the stages' order)
  const ordered = [...new Set(levels)];
  if (orderStageLevel === null) return ordered;
  const at = ordered.indexOf(orderStageLevel);
  const out: (number | "create-order")[] = [...ordered];
  // Right after the Create Order stage; if that stage has no steps, before the first later one (Default: Closed Won 5)
  const later = ordered.findIndex((l) => l > orderStageLevel && l <= 7);
  out.splice(at !== -1 ? at + 1 : later !== -1 ? later : ordered.length, 0, "create-order");
  return out;
};

/** What an approver can do with a step whose turn it is. */
export type StepAction = "approve" | "reject" | "skip";

export interface DealApprovalPerson {
  id: string;
  name: string;
}

export interface DealApprovalTemplateStep {
  id: string;
  fromStageLevel: number;
  stageName: string;
  nextStageName: string | null;
  sequence: number;
  /** Main stage it belongs to; null = the CRM stage itself (Default type) */
  mainStageName: string | null;
  stepName: string;
  description: string | null;
  approverUserIds: string[];
  /** After the order is approved: assigned in each project from its client's team, not here */
  assignedInProject?: boolean;
  approvers: DealApprovalPerson[];
}

/** Template type a Super Admin picks when creating a company's template (seeded per business unit). */
export interface DealTemplateType {
  id: string;
  code: string;
  name: string;
  description: string | null;
  /** The Super Admin defines the main stages */
  isCustom: boolean;
  businessUnitId: string | null;
  mainStages: { name: string | null; stageLevel: number; subSteps: { stepName: string; description?: string | null }[] }[];
}

/** Main stages of a template, in order: sub steps grouped by CRM stage + main stage name. */
export const groupMainStages = <T extends { fromStageLevel: number; sequence: number; mainStageName?: string | null }>(steps: T[]) => {
  const groups: { stageLevel: number; name: string | null; steps: T[] }[] = [];
  [...steps]
    .sort((a, b) => a.fromStageLevel - b.fromStageLevel || a.sequence - b.sequence)
    .forEach((s) => {
      const last = groups[groups.length - 1];
      const name = s.mainStageName ?? null;
      if (last && last.stageLevel === s.fromStageLevel && last.name === name) last.steps.push(s);
      else groups.push({ stageLevel: s.fromStageLevel, name, steps: [s] });
    });
  return groups;
};

/**
 * A main stage's label: its own name, else its CRM stage's name from the given stages
 * (the company's gated stages; Default ones when not given).
 */
export const mainStageLabel = (stageLevel: number, name: string | null, stages: GatedStage[] = DEAL_APPROVAL_STAGES) => {
  const stage = stages.find((s) => s.level === stageLevel);
  return name ? `${name}` : `${stage?.name ?? stageLevel}`;
};

export interface DealApprovalTemplate {
  id: string;
  companyId: string;
  companyName: string | null;
  name: string;
  description: string | null;
  isActive: boolean;
  /** Super Admin's switch: the company uses the template only once assigned */
  isAssigned: boolean;
  templateTypeId: string | null;
  templateTypeName: string | null;
  templateTypeIsCustom: boolean;
  /** The company's Create Order stage (Default: Legal 4); null = its deals don't create orders */
  orderStageLevel: number | null;
  /** The final stage a deal reaches once its order is closed (no approvals); null = none */
  completedStageName: string | null;
  /** Every step has an approver — only complete templates are applied */
  isComplete: boolean;
  stepsMissingApprovers: number;
  createdAt: string;
  updatedAt: string;
  steps: DealApprovalTemplateStep[];
  /** The template's main steps = the company's stages that can have approval sub steps, in order (fixed) */
  mainStages: {
    stageLevel: number;
    stageName: string;
    nextStageName: string | null;
    isDefault: boolean;
    createsOrder: boolean;
    /** Main steps can be added after this one: only once the order is fully approved (won stage onward) */
    allowsAddedMainSteps: boolean;
    /** Its sub steps get their approvers in each project (from the client's team), not in the template */
    assignedInProject?: boolean;
  }[];
}

export interface DealApprovalTemplatePayload {
  companyId?: string;
  templateTypeId?: string;
  name: string;
  description?: string | null;
  isActive?: boolean;
  isAssigned?: boolean;
  steps: { fromStageLevel: number; mainStageName?: string | null; stepName: string; description?: string | null; approverUserIds: string[] }[];
}

export interface ApproverCandidate {
  id: string;
  name: string;
  email: string;
  role: string;
}

/** Pick one before choosing approvers: only users whose permissions JSON has it = "full" are listed. */
export type ApproverPermissionFilter = "oms_sales" | "oms_operations";
export const APPROVER_PERMISSION_FILTERS: { value: ApproverPermissionFilter; label: string }[] = [
  { value: "oms_sales", label: "OMS Sales" },
  { value: "oms_operations", label: "OMS Operations" },
];

export type DealApprovalStepState = "approved" | "actionable" | "queued" | "waiting" | "skipped";

export interface DealApprovalStep {
  id: string;
  fromStageLevel: number;
  stageName: string;
  nextStageName: string | null;
  sequence: number;
  /** Main stage from the company's template; null = the CRM stage itself */
  mainStageName: string | null;
  stepName: string;
  description: string | null;
  /** skipped = passed over by an approver; counts as done like approved */
  /** Added to this project only (not from the template) */
  isAdded?: boolean;
  /** After the order is approved: assigned in this project from the client's team */
  assignedInProject?: boolean;
  status: "pending" | "approved" | "rejected" | "skipped";
  state: DealApprovalStepState;
  approvers: DealApprovalPerson[];
  isApprover: boolean;
  canAct: boolean;
  actedByName: string | null;
  actedAt: string | null;
  remark: string | null;
  /** Approvers' comments on the sub step, oldest first */
  comments: DealApprovalStepComment[];
  /** The viewer is an approver and the step is still open */
  canComment: boolean;
  /** The viewer's own comments — at least one is needed before "Mark as completed" */
  myCommentCount: number;
}

export interface DealApprovalStepComment {
  id: string;
  comment: string;
  userId: string;
  userName: string;
  createdAt: string;
}

/** One OMS order approval level (Super Admin → Order Approvers) of the deal's order. */
export interface DealOrderLevel {
  priority: number;
  /** e.g. "L1 Ops" */
  label: string;
  /** current = the level the order waits on now; waiting = later level or order not created yet */
  status: "approved" | "skipped" | "rejected" | "current" | "waiting";
  approvers: string[];
  actedByName: string | null;
  actedAt: string | null;
  comments: string | null;
}

export interface DealOrderStep {
  state: "waiting" | "ready" | "pending" | "approved" | "skipped";
  /** The company's Create Order stage and the stage the deal moves to after (Default: Legal, Closed Won) */
  orderStageName?: string | null;
  nextStageName?: string | null;
  orderId: number | null;
  orderStatus: string | null;
  /** OMS level the order waits on, e.g. "L2 BH" */
  levelLabel: string | null;
  /** L1–L4: the order's approval levels, or the company's levels before the order exists */
  levels: DealOrderLevel[];
}

export interface DealApprovalRow {
  id: string;
  dealId: number;
  dealName: string;
  dealAmount: number;
  dealCurrency: string;
  dealStage: number;
  dealStageName: string;
  /** The company's Create Order stage (Default: Legal 4); null = its deals don't create orders */
  orderStageLevel: number | null;
  /** Set once Create Order ran; at Legal the deal then waits on that order's approvals */
  orderId: number | null;
  /** The Create Order step, decided by the order's OMS approvals */
  orderStep: DealOrderStep;
  companyId: string;
  companyName: string | null;
  /** The client (CRM company) the deal is for */
  crmCompanyId: string;
  crmCompanyName: string | null;
  ownerName: string | null;
  templateName: string;
  status: "in_progress" | "completed" | "closed_lost";
  approved: number;
  total: number;
  stageApproved: number;
  stageTotal: number;
  waitingOn: { stepId: string; stepName: string; status: string; approvers: string[] } | null;
  canAct: boolean;
  /** At the Completed stage (its order is closed): can be hidden from Projects */
  isCompleted: boolean;
  /** Hidden from Projects (shown only under "Hidden"); null = shown */
  hiddenAt: string | null;
  /** The final stage the deal reaches once its order is closed (no approvals); null = the company has none */
  completedStageName: string | null;
  /** The viewer can approve / reject the order's current approval level from the project */
  orderCanAct: boolean;
  /** Super Admin / the company's Admin can add main steps and sub steps to this project */
  canAddSteps: boolean;
  /** The order's creator (or Super Admin), once the order is approved: can assign the after-order sub tasks */
  canAssign?: boolean;
  /** Who created the deal's order (assigns the after-order sub tasks) */
  orderCreatorName?: string | null;
  /** Where steps can be added: the first approval stage (Proposal), the won stage, the last stage */
  firstStageLevel: number | null;
  wonStageLevel: number | null;
  lastStageLevel: number;
  createdAt: string;
  updatedAt: string;
  steps: DealApprovalStep[];
}

export interface DealApprovalDetail extends DealApprovalRow {
  history: { id: string; action: "approved" | "rejected" | "skipped" | "reopened"; stepName: string | null; remark: string | null; actedByName: string | null; actedAt: string }[];
}

/** Attached to each deal by GET /crm/deals and /crm/deals/:id (null when the company has no template). */
export interface DealApprovalGate {
  templateName: string;
  status: "in_progress" | "completed";
  approved: number;
  total: number;
  stageApproved: number;
  stageTotal: number;
  stagePendingSteps: { name: string; status: string }[];
  /** The main step the deal is on at its stage (the stage itself or a named one; custom = added to this project only); null = no sub steps */
  mainStep: { name: string; custom: boolean; approved: number; total: number; done: boolean } | null;
  canMoveForward: boolean;
  canMoveBack: boolean;
}

export interface DealApprovalListParams {
  page: number;
  limit: number;
  search?: string;
  companyId?: string;
  dealId?: number;
  status?: "in_progress" | "completed" | "closed_lost" | "my_action" | "hidden" | "all";
  /** "client": page and limit count clients, each returned with all its matching deals */
  groupBy?: "client";
}

/** API error message from an axios error, else a fallback. */
export const apiErrorMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const BASE = "/crm-deal-approvals";

// Templates (Super Admin) -------------------------------------------------------------------------

/** All templates (Super Admin), or one company's (Super Admin / that company's Admin). */
export const useDealApprovalTemplates = (companyId?: string | null, enabled = true) =>
  useQuery({
    queryKey: ["deal-approval-templates", companyId ?? "all"],
    queryFn: async () =>
      (await api.get<{ data: DealApprovalTemplate[] }>(`${BASE}/templates`, { params: companyId ? { companyId } : undefined })).data.data,
    enabled,
  });

/** Template types offered for a business unit (its own + the global Default / Custom). */
export const useDealTemplateTypes = (businessUnitId?: string | null, companyId?: string | null) =>
  useQuery({
    queryKey: ["deal-approval-templates", "types", businessUnitId, companyId ?? null],
    // With the company: only Custom when the company has its own deal stages
    queryFn: async () =>
      (await api.get<{ data: DealTemplateType[] }>(`${BASE}/templates/types`, { params: { businessUnitId, companyId } })).data.data,
    enabled: !!businessUnitId,
  });

/** What a company lacks before a template can be made for it (OMS, CRM, a Project Management tool, no template yet). */
export const fetchCompanyTemplateReadiness = async (companyId: string) =>
  (
    await api.get<{ data: { companyId: string; companyName: string | null; ready: boolean; missing: string[] } }>(
      `${BASE}/templates/company-readiness/${companyId}`
    )
  ).data.data;

/** The company's users that can approve, fetched only once a permission filter is picked. */
export const useApproverCandidates = (companyId?: string, permission?: ApproverPermissionFilter) =>
  useQuery({
    queryKey: ["deal-approval-templates", "candidates", companyId, permission],
    queryFn: async () =>
      (await api.get<{ data: ApproverCandidate[] }>(`${BASE}/templates/approver-candidates`, { params: { companyId, permission } })).data.data,
    enabled: !!companyId && !!permission,
  });

export const useSaveDealApprovalTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id?: string; payload: DealApprovalTemplatePayload }) =>
      (id ? await api.put(`${BASE}/templates/${id}`, payload) : await api.post(`${BASE}/templates`, payload)).data as {
        message: string;
        data: DealApprovalTemplate;
      },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal-approval-templates"] }),
  });
};

export const useDeleteDealApprovalTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`${BASE}/templates/${id}`)).data as { message: string },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal-approval-templates"] }),
  });
};

// Deal approvals ----------------------------------------------------------------------------------

export const useDealApprovals = (params: DealApprovalListParams) =>
  useQuery({
    queryKey: ["deal-approvals", params],
    queryFn: async () =>
      (
        await api.get<{ data: DealApprovalRow[]; total: number; page: number; limit: number }>(BASE, {
          params: { ...params, status: params.status === "all" ? undefined : params.status },
        })
      ).data,
    placeholderData: keepPreviousData,
  });

export const useDealApprovalDetail = (dealId?: number) =>
  useQuery({
    queryKey: ["deal-approvals", "deal", dealId],
    queryFn: async () => (await api.get<{ data: DealApprovalDetail }>(`${BASE}/deals/${dealId}`)).data.data,
    enabled: !!dealId,
    retry: false,
  });

export const useActOnDealApprovalStep = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ stepId, action, remark }: { stepId: string; action: StepAction; remark?: string }) =>
      (await api.post(`${BASE}/steps/${stepId}/action`, { action, remark })).data as { message: string },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-approvals"] });
      // Deal cards show the approval gate
      qc.invalidateQueries({ queryKey: ["crmDeals"] });
      qc.invalidateQueries({ queryKey: ["crmDeal"] });
    },
  });
};

/** An approver adds a comment on a deal's sub step. */
/** ✕ on a Completed project hides it from Projects for everyone; `hidden: false` restores it. */
export const useSetDealApprovalHidden = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, hidden }: { id: string; hidden: boolean }) =>
      (await api.patch(`${BASE}/${id}/hidden`, { hidden })).data as { message: string },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal-approvals"] }),
  });
};

export interface AddProjectStepsPayload {
  kind: "main" | "sub";
  stageLevel: number;
  afterStepId?: string | null;
  atStart?: boolean;
  mainStageName?: string | null;
  steps: { stepName: string; description?: string | null; approverUserIds: string[] }[];
}

/** Projects → add a main step / sub steps to one project (its template is untouched). */
export const useAddProjectSteps = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: AddProjectStepsPayload }) =>
      (await api.post(`${BASE}/${id}/steps`, payload)).data as { message: string },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-approvals"] });
      qc.invalidateQueries({ queryKey: ["crmDeals"] });
      qc.invalidateQueries({ queryKey: ["crmDeal"] });
    },
  });
};

export const useAddDealStepComment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ stepId, comment }: { stepId: string; comment: string }) =>
      (await api.post(`${BASE}/steps/${stepId}/comments`, { comment })).data as { message: string },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal-approvals"] }),
  });
};

export const checkIsDealApprover = async (): Promise<{ isApprover: boolean }> => {
  const response = await api.get(`${BASE}/me/is-approver`);
  return response.data;
};

/** A member of the deal client's team (who after-order sub tasks can be assigned to) */
export interface ClientTeamMember {
  id: string;
  name: string;
  role: string | null;
}

/** The deal client's team for a project (order creator, Super Admin, the company's Admin) */
export const useProjectClientTeam = (approvalId?: string, enabled = true) =>
  useQuery({
    queryKey: ["deal-approvals", approvalId, "client-team"],
    queryFn: async () => (await api.get<{ data: ClientTeamMember[] }>(`${BASE}/${approvalId}/client-team`)).data.data,
    enabled: !!approvalId && enabled,
  });

/** The order's creator assigns an after-order sub task to people from the client's team */
export const useAssignProjectStep = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ stepId, approverUserIds }: { stepId: string; approverUserIds: string[] }) =>
      (await api.patch(`${BASE}/steps/${stepId}/assignees`, { approverUserIds })).data as { message: string },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-approvals"] });
    },
  });
};
